import { useState, useEffect, useCallback, useMemo } from "react";
import {
  AsYouType,
  formatIncompletePhoneNumber,
  parsePhoneNumber,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";
import type { Country } from "./InputPhone";
import {
  allCountries,
  getCountryByCode,
  getPlaceholderByCode,
} from "./countries";

export interface UsePhoneNumberOptions {
  /** Initial country code (ISO 3166-1 alpha-2, e.g., "US", "MY") */
  initialCountry?: string;
  /** Initial phone number value */
  initialValue?: string;
  /** Callback when phone number changes */
  onChange?: (phoneNumber: string) => void;
  /** Callback when country changes */
  onCountryChange?: (country: Country | undefined) => void;
}

export interface UsePhoneNumberReturn {
  /** Raw phone number (with country code) */
  phoneNumber: string;
  /** Formatted phone number for display */
  formattedPhoneNumber: string;
  /** Currently detected/selected country */
  country: Country | undefined;
  /** All available countries */
  countries: Country[];
  /** Country-specific placeholder mask */
  placeholder: string | undefined;
  /** Handle phone number input change */
  handlePhoneChange: (value: string) => void;
  /** Handle country selection change */
  handleCountryChange: (country: Country) => void;
  /** Set phone number programmatically */
  setPhoneNumber: (value: string) => void;
}

/**
 * Normalize phone number to E.164 format
 */
const getNormalizedPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";

  const asYouType = new AsYouType();
  asYouType.input(phoneNumber);
  const parsed = asYouType.getNumber();

  if (parsed?.number) {
    return parsed.number;
  }

  // If parsing fails, ensure it starts with +
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  if (digitsOnly.length > 0 && !phoneNumber.startsWith("+")) {
    return `+${digitsOnly}`;
  }

  return phoneNumber;
};

/**
 * Detect country from phone number
 */
const detectCountryFromPhone = (
  phoneNumber: string,
  currentCountryCode?: string
): string | undefined => {
  if (!phoneNumber) return currentCountryCode;

  const digitsOnly = phoneNumber.replace(/\D/g, "");
  if (digitsOnly.length === 0) return currentCountryCode;

  // Ensure phone starts with + for parsing
  const normalizedPhone = phoneNumber.startsWith("+")
    ? phoneNumber
    : `+${digitsOnly}`;

  // Try parsing with parsePhoneNumber (most accurate)
  try {
    const parsed = parsePhoneNumber(normalizedPhone, {
      defaultCountry: currentCountryCode as CountryCode,
      extract: false,
    });
    if (parsed?.country) {
      return parsed.country;
    }
  } catch {
    // Parsing failed, try other methods
  }

  // Try AsYouType detection
  try {
    const asYouType = new AsYouType(currentCountryCode as CountryCode);
    asYouType.input(normalizedPhone);
    if (asYouType.country) {
      return asYouType.country;
    }
  } catch {
    // AsYouType failed
  }

  // Check if current country's calling code matches
  if (currentCountryCode) {
    try {
      const callingCode = getCountryCallingCode(
        currentCountryCode as CountryCode
      );
      if (digitsOnly.startsWith(callingCode)) {
        return currentCountryCode;
      }
    } catch {
      // Invalid country code
    }
  }

  return currentCountryCode;
};

/**
 * Hook for managing phone number input with formatting, country detection
 * and a country-specific placeholder.
 *
 * @example
 * ```tsx
 * const {
 *   placeholder,
 *   phoneNumber,
 *   formattedPhoneNumber,
 *   country,
 *   countries,
 *   handlePhoneChange,
 *   handleCountryChange,
 * } = usePhoneNumber({
 *   initialCountry: "MY",
 *   onChange: (phone) => console.log("Phone:", phone),
 * });
 * ```
 */
export const usePhoneNumber = (
  options: UsePhoneNumberOptions = {}
): UsePhoneNumberReturn => {
  const {
    initialCountry,
    initialValue = "",
    onChange,
    onCountryChange,
  } = options;

  const getInitialCountry = (): Country | undefined => {
    if (initialCountry) {
      return getCountryByCode(initialCountry);
    }
    return undefined;
  };

  const [phoneNumber, setPhoneNumberState] = useState<string>(() => {
    if (initialValue) {
      return getNormalizedPhoneNumber(initialValue);
    }
    return "";
  });

  const [country, setCountry] = useState<Country | undefined>(
    getInitialCountry
  );

  const formattedPhoneNumber = phoneNumber
    ? formatIncompletePhoneNumber(
        phoneNumber,
        country?.code as CountryCode | undefined
      )
    : "";

  const handlePhoneChange = useCallback(
    (value: string) => {
      let normalizedValue = value;

      // If input doesn't start with +, add it for proper parsing
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 0 && !value.startsWith("+")) {
        normalizedValue = `+${digitsOnly}`;
      }

      // Special handling for Russian numbers: convert "+8" prefix to "+7" while preserving remaining digits
      if (country?.code === "RU" && normalizedValue.startsWith("+8")) {
        const rest = normalizedValue.slice(2);
        normalizedValue = `+7${rest}`;
      }

      const detectedCountryCode = detectCountryFromPhone(
        normalizedValue,
        country?.code
      );

      if (detectedCountryCode && detectedCountryCode !== country?.code) {
        const newCountry = getCountryByCode(detectedCountryCode);
        if (newCountry) {
          setCountry(newCountry);
          onCountryChange?.(newCountry);
        }
      }

      setPhoneNumberState(normalizedValue);
      onChange?.(normalizedValue);
    },
    [country?.code, onChange, onCountryChange]
  );

  const handleCountryChange = useCallback(
    (newCountry: Country) => {
      const previousCountry = country;
      setCountry(newCountry);
      onCountryChange?.(newCountry);

      // If we have a phone number, update it with the new country code
      if (phoneNumber) {
        let newPhoneNumber = phoneNumber;

        // Remove old country code if present
        if (previousCountry) {
          const oldDialCode = previousCountry.dialCode.replace("+", "");
          const digitsOnly = phoneNumber.replace(/\D/g, "");
          if (digitsOnly.startsWith(oldDialCode)) {
            newPhoneNumber = `+${digitsOnly.slice(oldDialCode.length)}`;
          }
        }

        // Add new country code
        const newDialCode = newCountry.dialCode.replace("+", "");
        const currentDigits = newPhoneNumber.replace(/\D/g, "");
        if (!currentDigits.startsWith(newDialCode)) {
          newPhoneNumber = `+${newDialCode}${currentDigits}`;
        }

        setPhoneNumberState(newPhoneNumber);
        onChange?.(newPhoneNumber);
      }
    },
    [country, phoneNumber, onChange, onCountryChange]
  );

  const setPhoneNumber = useCallback(
    (value: string) => {
      const normalized = getNormalizedPhoneNumber(value);
      setPhoneNumberState(normalized);
      onChange?.(normalized);

      const detectedCode = detectCountryFromPhone(normalized, country?.code);
      if (detectedCode) {
        const detectedCountry = getCountryByCode(detectedCode);
        if (detectedCountry && detectedCountry.code !== country?.code) {
          setCountry(detectedCountry);
          onCountryChange?.(detectedCountry);
        }
      }
    },
    [country?.code, onChange, onCountryChange]
  );

  useEffect(() => {
    if (initialValue && initialValue !== phoneNumber) {
      const normalized = getNormalizedPhoneNumber(initialValue);
      setPhoneNumberState(normalized);

      const detectedCode = detectCountryFromPhone(normalized, initialCountry);
      if (detectedCode) {
        const detectedCountry = getCountryByCode(detectedCode);
        if (detectedCountry) {
          setCountry(detectedCountry);
        }
      }
    }
  }, [initialValue]);

  const placeholder = useMemo(
    () => getPlaceholderByCode(country?.code),
    [country?.code]
  );

  return {
    phoneNumber,
    formattedPhoneNumber,
    country,
    countries: allCountries,
    placeholder,
    handlePhoneChange,
    handleCountryChange,
    setPhoneNumber,
  };
};

export default usePhoneNumber;
